import { Int } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { MailProcessingFilter } from '../../information-extraction/db/MailProcessingFilter.js';
import { resolveStatsSenderAddress } from '../../information-extraction/server/stats-sender-pipeline.js';
import { UserNotification } from '../db/UserNotification.js';

/** Matches no document — the answer for a scope that resolves to nobody. */
const NO_RECIPIENT = '\0__no_recipient__';

/**
 * Turns the page's sender scope into a recipient: a request is attributed to the employee who
 * forwarded it, and that is exactly who its questions are addressed to.
 */
async function resolveRecipientFilter(filter?: MailProcessingFilter): Promise<Record<string, unknown>> {
  const senderAddress = await resolveStatsSenderAddress(filter);
  if (!senderAddress) return {};

  // Stored addresses keep the casing their mail client used; the scope comes in lowercased.
  const users = await getRawCollection<EntityData<AdornisUser>>(AdornisUser._collectionName);
  const user = await users.findOne(
    { $expr: { $eq: [{ $toLower: '$email' }, senderAddress] } },
    { projection: { _id: 1 } },
  );
  return { recipientUserID: user?._id ?? NO_RECIPIENT };
}

/**
 * How many Rückfragen the platform had to raise — the number the customer is billed by, for
 * the selected period or for all time.
 *
 * Counted over the notifications, not over the extractions behind them: a technical abort can
 * happen before its request was ever written, and it is a raised question all the same.
 */
export const getNotificationCount = registerQuery({
  type: () => Int,
  operationName: 'getNotificationCount',
  resolve: (filter?: MailProcessingFilter) => {
    return async () => {
      const collection = await getRawCollection<EntityData<UserNotification>>(UserNotification._collectionName);
      return collection.countDocuments({
        ...(filter?.getMongoDateFilter() ?? {}),
        ...(await resolveRecipientFilter(filter)),
      });
    };
  },
  params: [{ type: () => MailProcessingFilter, name: 'filter' }],
});
